import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Tabs, Tab, Table } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { getTableBd, insertTableBd } from '../../config/functions';
import { toast } from 'react-toastify';

const ImportFromExcelModal = ({
    show, handleClose,
    rig, rigName, fecha, user, operadora, campo, pozo,
    getDetalleEmpleados, getEncabezado
}) => {

    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [errors, setErrors] = useState([]);
    const [parsedData, setParsedData] = useState(null);
    const [activeTab, setActiveTab] = useState('json');

    // 1. Leer el archivo Excel
    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        setFile(selectedFile);
        setErrors([]);
        setPreview(null);
        setParsedData(null);
        setActiveTab('json'); // Reset tab to JSON view on new file

        if (selectedFile) {
            const reader = new FileReader();
            reader.onload = (evt) => {
                const data = new Uint8Array(evt.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const result = parseWorkbook(workbook);
                setPreview(result.preview);
                setParsedData(result.data);
                setErrors(result.errors);
            };
            reader.readAsArrayBuffer(selectedFile);
        }
    };

    // 2. Parsear la hoja principal por secciones reales
    const parseWorkbook = (workbook) => {
        let errors = [];
        let preview = {};
        let data = {};

        // Toma la primera hoja
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        if (!sheet) {
            errors.push('No se encontró la hoja principal');
            return { errors, preview, data };
        }

        // Convierte la hoja a array de arrays
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });

        // Validación: la celda A2 debe decir "Operador:"
        if (!rows[1] || typeof rows[1][0] !== 'string' || rows[1][0].trim().toLowerCase() !== 'operador:') {
            errors.push('El archivo no cumple con la estructura esperada. La celda A2 debe contener "Operador:".');
            return { errors, preview, data };
        }

        // Función para buscar el índice de una fila que cumpla una condición
        const findRowIndex = (testFn, from = 0) => rows.findIndex((row, idx) => idx >= from && testFn(row));


        // 1. Encabezado (como objeto estructurado)
        const encabezadoObj = {
            Operador: operadora,//rows[1]?.[1] || '',
            Rig: rigName, // rows[1]?.[3] || '',
            Campo: campo,//rows[1]?.[7] || '',
            Pozo: pozo,//rows[2]?.[1] || '',
            Fecha: fecha, //rows[2]?.[4] || '',
            Verificado: ''//rows[2]?.[7] || ''
        };
        preview.encabezado = encabezadoObj;
        data.encabezado = encabezadoObj;


        // 2. Actividades
        const actividadesHeaderIdx = findRowIndex(row => row[0] === 'Desde' && row[1] === 'Hasta');
        let actividades = [];
        if (actividadesHeaderIdx !== -1) {

            let endIdx = rows.length;      // Buscar la primera fila vacía o encabezado de cuadrilla después del header
            for (let i = actividadesHeaderIdx + 1; i < rows.length; i++) {
                const row = rows[i];
                if (
                    row.every(cell => !cell || cell === '') ||
                    (row[0] && row[1] && row[0].toString().trim().toLowerCase() === 'cargo' && row[1].toString().trim().toLowerCase() === 'nombre')
                ) {
                    endIdx = i;
                    break;
                }
            }
            actividades = rows.slice(actividadesHeaderIdx + 1, endIdx)
                .filter(row => row[0] && row[1])
                .map(row => ({
                    desde: row[0],
                    hasta: row[1],
                    total: Number(row[2]) || 0,
                    operacion: row[3] || '',
                    actividad: row[4] || '',
                    observacion: row[6] || ''
                }));
            preview.actividades = actividades;
            data.actividades = actividades;
        } else {
            errors.push('No se encontró la tabla de actividades');
        }


        // 3. Cuadrillas (formato horizontal, bloques de 3 columnas, separando superiores e inferiores)
        const cuadrillaNombresFilaIdx = findRowIndex(row =>
            row.some(cell => typeof cell === 'string' && cell.toUpperCase().includes('CUADRILLA'))
        );
        let cuadrillas = {};
        if (cuadrillaNombresFilaIdx !== -1) {
            // Detectar bloques de cuadrillas superiores (turnos)
            const cuadrillaTitlesRow = rows[cuadrillaNombresFilaIdx];
            let cuadrillaBlocks = [];
            for (let col = 0; col < cuadrillaTitlesRow.length; col += 3) {
                const title = cuadrillaTitlesRow[col];
                if (title && typeof title === 'string' && title.toUpperCase().includes('CUADRILLA')) {
                    cuadrillaBlocks.push({
                        name: title,
                        startCol: col
                    });
                }
            }
            // Leer datos de cuadrillas superiores
            let cuadrillaDataRows = [];
            for (let i = cuadrillaNombresFilaIdx + 2; i < rows.length; i++) { // +2 para saltar encabezado de columnas
                const row = rows[i];
                // Si todas las cuadrillas están vacías en esta fila, termina
                const allEmpty = cuadrillaBlocks.every(block => {
                    return !row[block.startCol] && !row[block.startCol + 1] && !row[block.startCol + 2];
                });
                // Si detecta el inicio de una cuadrilla inferior, termina
                const hasInferiorBlock = row.some(cell => {
                    if (!cell || typeof cell !== 'string') return false;
                    const up = cell.toUpperCase();
                    return up.includes('CUADRILLA DESCANSO') || up.includes('OBREROS DE PATIO') || up.includes('STAFF');
                });
                if (allEmpty || hasInferiorBlock) break;
                cuadrillaDataRows.push(row);
            }
            cuadrillaBlocks.forEach(block => {
                const horarioMatch = block.name.match(/\d{2}:\d{2}-\d{2}:\d{2}/);
                const horario = horarioMatch ? horarioMatch[0] : '00:00-00:00'; // Default para casos como descanso

                cuadrillas[block.name] = cuadrillaDataRows
                    .map(row => ({
                        cargo: row[block.startCol] || '',
                        nombre: row[block.startCol + 1] || '',
                        horas: row[block.startCol + 2] || null,
                        horario: horario
                    }))
                    .filter(item => item.nombre && item.nombre.toString().trim().toLowerCase() !== 'nombre'); // Solo si hay nombre y no es encabezado
            });

            // Buscar cuadrillas inferiores (descanso, obreros de patio, staff)
            let idxInf = cuadrillaNombresFilaIdx + cuadrillaDataRows.length + 2;
            while (idxInf < rows.length) {
                const row = rows[idxInf];
                if (!row) break;
                // Detectar nombre de bloque inferior
                for (let col = 0; col < row.length; col += 3) {
                    const title = row[col];
                    if (title && typeof title === 'string' && (
                        title.toUpperCase().includes('CUADRILLA DESCANSO') ||
                        title.toUpperCase().includes('OBREROS DE PATIO') ||
                        title.toUpperCase().includes('STAFF')
                    )) {
                        // Leer datos de este bloque
                        let blockName = title;
                        let blockRows = [];
                        // Saltar encabezado
                        let j = idxInf + 2;
                        if (blockName.toUpperCase().includes('STAFF')) {
                            // Leer hasta 10 empleados válidos para STAFF (ignorando filas vacías, incluso al final)
                            let empleadosLeidos = 0;
                            while (j < rows.length && empleadosLeidos < 10) {
                                const r = rows[j];
                                j++;
                                if (!r) continue;
                                const nombre = r[col + 1];

                                if (nombre && nombre.toString().trim().toLowerCase() !== 'nombre') {
                                    blockRows.push(r);
                                    empleadosLeidos++;
                                }
                            }
                        } else {
                            while (j < rows.length) {
                                const r = rows[j];
                                if (!r || (!r[col] && !r[col + 1] && !r[col + 2])) break;
                                blockRows.push(r);
                                j++;
                            }
                        }
                        cuadrillas[blockName] = blockRows
                            .map(r => {
                                const cargo = r[col] || '';
                                let horas = r[col + 2];
                                let horario = '00:00-00:00'; // Default

                                if (blockName.toUpperCase().includes('DESCANSO')) {
                                    horas = 0;
                                } else if (blockName.toUpperCase().includes('STAFF')) {
                                    const horarioMatch = cargo.match(/\d{2}:\d{2}-\d{2}:\d{2}/);
                                    if (cargo.toUpperCase().includes('SUP') && horarioMatch) {
                                        horario = horarioMatch[0];
                                        const [startStr, endStr] = horario.split('-');
                                        const [startHour, startMinute] = startStr.split(':').map(Number);
                                        const [endHour, endMinute] = endStr.split(':').map(Number);

                                        let startDate = new Date(0);
                                        startDate.setHours(startHour, startMinute, 0, 0);

                                        let endDate = new Date(0);
                                        endDate.setHours(endHour, endMinute, 0, 0);

                                        if (endDate <= startDate) { // Handles overnight shifts
                                            endDate.setDate(endDate.getDate() + 1);
                                        }

                                        const diffMs = endDate - startDate;
                                        horas = diffMs / (1000 * 60 * 60);

                                    } else {
                                        horas = 24;
                                    }
                                }

                                return {
                                    cargo: cargo,
                                    nombre: r[col + 1] || '',
                                    horas: horas || 0,
                                    horario: horario
                                };
                            })
                            .filter(item => item.nombre && item.nombre.toString().trim().toLowerCase() !== 'nombre');
                    }
                }
                idxInf++;
            }
        }
        preview.cuadrillas = cuadrillas;
        data.cuadrillas = cuadrillas;
        return { errors, preview, data };
    };

    // Limpia los datos al cerrar el modal
    const handleCloseInternal = async () => {
        setFile(null);
        setPreview(null);
        setErrors([]);
        setParsedData(null);
        setActiveTab('json'); // Reset tab on close
        handleClose();

        // Llamar a las funciones de actualización de encabezado y detalle de empleados
        await getEncabezado();
        await getDetalleEmpleados();
    };

    // 3. Subir los datos validados
    const handleImport = async () => {

        if (errors.length > 0 || !parsedData) return;

        //Validar que no hay un reporte guardado para esa fecha
        const fecha = parsedData.encabezado.Fecha;
        const data = await getTableBd('getReporteDiarioERigFecha/' + rig + '/' + fecha)
        if (data && data.length > 0) {
            toast.warning('Ya existe un reporte cargado para esta fecha, valide los datos antes de continuar.');
            return;
        }

        await guardarEncabezado();

        handleCloseInternal();
    };

    const guardarEncabezado = async () => {

        if (operadora > 0 && campo > 0 && pozo > 0) {
            const fechaSend = new Date(fecha);
            const dataSend = {
                operadora: parseInt(operadora),
                campo: parseInt(campo),
                pozo: parseInt(pozo),
                fecha: fechaSend,
                usuario: user,
                rig: rig,
                usuario_verifica:''
            }

            const responseEncabezado = await insertTableBd('createregistrediarioE', dataSend)
            if (responseEncabezado) {
                const response = await getTableBd('getReporteDiarioERigFecha/' + rig + '/' + fecha)
                await guardarEmpleados24(response[0]?.id);
            } else {
                toast.error('Error al guardar el encabezado')
            }
        }
    }

    const guardarEmpleados24 = async (idrde) => {

        const staffKey = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('STAFF'));
        const staffData = staffKey ? parsedData.cuadrillas[staffKey] : [];

        const empleados24 = staffData.filter(emp => emp.horas === 24);
        const supervisores = staffData.filter(emp => emp.horas !== 24);

        try {
            const promises = empleados24.map((empleado) => {
                const dataSend = {
                    id_rde: parseInt(idrde),
                    empleado: empleado.nombre.toUpperCase(),
                    cargo: empleado.cargo.toUpperCase(),
                    horas: empleado.horas,
                    horario: empleado.horario,
                    usuario: user,
                };
                return insertTableBd('createregistrediarioDE', dataSend);
            });
            await Promise.all(promises);

            const supervisorPromises = supervisores.map((empleado) => {
                const dataSend = {
                    id_rde: parseInt(idrde),
                    empleado: empleado.nombre.toUpperCase(),
                    cargo: 'SUPERVISOR',
                    horas: empleado.horas,
                    horario: empleado.horario,
                    usuario: user,
                };
                return insertTableBd('createregistrediarioDE', dataSend);
            });
            await Promise.all(supervisorPromises);
            await guardarCuadrillas(idrde);

        } catch (error) {
            toast.error('Ocurrió un error al guardar algunos empleados, intente manualmente');
        }
    }

    const guardarCuadrillas = async (idrde) => {

        const cuadrilla1 = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('CUADRILLA 06:00-14:00'));
        const cuadrilla2 = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('CUADRILLA 14:00-22:00'));
        const cuadrilla3 = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('CUADRILLA 22:00-06:00'));
        const cuadrilla4 = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('CUADRILLA DESCANSO'));
        const cuadrilla5 = Object.keys(parsedData.cuadrillas).find(key => key.toUpperCase().startsWith('OBREROS DE PATIO'));

        const cuadrilla1Data = parsedData.cuadrillas[cuadrilla1] || [];
        const cuadrilla2Data = parsedData.cuadrillas[cuadrilla2] || [];
        const cuadrilla3Data = parsedData.cuadrillas[cuadrilla3] || [];
        const cuadrilla4Data = parsedData.cuadrillas[cuadrilla4] || [];
        const cuadrilla5Data = parsedData.cuadrillas[cuadrilla5] || [];

        const allCuadrillas = [...cuadrilla1Data, ...cuadrilla2Data, ...cuadrilla3Data, ...cuadrilla4Data, ...cuadrilla5Data];

        const cuadrillaPromises = allCuadrillas.map((empleado) => {
            const dataSend = {
                id_rde: parseInt(idrde),
                empleado: empleado.nombre.toUpperCase(),
                cargo: empleado.cargo.toUpperCase(),
                horas: empleado.horas,
                horario: empleado.horario || '00:00-00:00',
                usuario: user,
            };
            return insertTableBd('createregistrediarioDE', dataSend);
        });
        try {
            await Promise.all(cuadrillaPromises);
            await guardarActividades(idrde);
        } catch (error) {
            toast.error('Ocurrió un error al guardar algunas cuadrillas, intente manualmente');
        }
    }

    const guardarActividades = async (idrde) => {

        const actividades = parsedData.actividades || [];
        let guardadas = 0;
        let fallidas = 0;

        for (const actividad of actividades) {
            const dataSend = {
                id_rde: parseInt(idrde),
                desde: actividad.desde,
                hasta: actividad.hasta,
                total: actividad.total,
                operacion: actividad.operacion.toLocaleUpperCase(),
                actividad: actividad.actividad.toLocaleUpperCase(),
                observacion: actividad.observacion,
                usuario: user
            };
            try {
                await insertTableBd('createregistrediarioDD', dataSend);
                guardadas++;
            } catch (error) {
                fallidas++;
            }
        }

        if (fallidas === 0) {
            toast.success('Importación exitosa: ' + guardadas + ' actividades guardadas');
        } else if (guardadas > 0) {
            toast.warning('Se guardaron ' + guardadas + ' de ' + actividades.length + ' actividades. ' + fallidas + ' fallaron.');
        } else {
            toast.error('Error al guardar las actividades, intente manualmente');
        }
        handleCloseInternal();
    }

    return (
        <Modal
            show={show}
            onHide={handleCloseInternal}
            size="xl"
            backdrop="static"
            centered>

            <Modal.Header closeButton>
                <div className="d-flex justify-content-between w-100 align-items-center">
                    <Modal.Title>
                        Importar RDO desde Excel <br />
                        <small className="text-muted" style={{ fontSize: 12 }}> ({rigName}) -- {fecha}</small>
                    </Modal.Title>
                    <div className="text-center">
                        <a
                            href="/files/PlantillaImpor.xlsx"
                            download="PlantillaImpor.xlsx"
                            className="btn btn-sm btn-success">
                            <i className="fa-solid fa-download me-2"></i>
                            Plantilla
                        </a>
                        <small className="d-block text-muted mt-1">Diligenciar solo campos en Amarillo</small>
                    </div>
                </div>
            </Modal.Header>
            <Modal.Body>
                <Form.Group>
                    <Form.Label>Selecciona el archivo Excel</Form.Label>
                    <Form.Control type="file" accept=".xlsx,.xls" onChange={handleFileChange} />
                </Form.Group>
                {errors.length > 0 && (
                    <Alert variant="danger" className="mt-3">
                        <ul>{errors.map((err, i) => <li key={i}>{err}</li>)}</ul>
                    </Alert>
                )}
                {preview && (
                    <div className="mt-3">
                        <h6>Vista previa de los datos:</h6>
                        <Tabs
                            id="preview-tabs"
                            activeKey={activeTab}
                            onSelect={(k) => setActiveTab(k)}
                            className="mb-3"
                        >
                            <Tab eventKey="json" title="JSON">
                                <pre style={{ fontSize: 12, maxHeight: '400px', overflowY: 'auto' }}>
                                    {JSON.stringify(preview, null, 2)}
                                </pre>
                            </Tab>
                            <Tab eventKey="tables" title="Vista de Tablas">
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    {/* Encabezado */}

                                    <Table striped bordered hover size="sm" responsive style={{ fontSize: 12 }}>
                                        <tbody>
                                            <tr><th colSpan={6} className='text-center table-info'>ENCABEZADO</th></tr>
                                            <tr>
                                                <td><strong>Operador:</strong></td>
                                                <td>{preview.encabezado?.Operador}</td>
                                                <td><strong>Rig:</strong></td>
                                                <td>{/* preview.encabezado.Rig */ rigName}</td>
                                                <td><strong>Campo:</strong></td>
                                                <td>{preview.encabezado?.Campo}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Pozo:</strong></td>
                                                <td>{preview.encabezado?.Pozo}</td>
                                                <td><strong>Fecha:</strong></td>
                                                <td>{preview.encabezado?.Fecha}</td>
                                                <td>{/* <strong>Verificado:</strong> */}</td>
                                                <td>{/* {preview.encabezado.Verificado} */}</td>
                                            </tr>
                                        </tbody>
                                    </Table>

                                    {/* Actividades */}
                                    <Table striped bordered hover size="sm" responsive style={{ fontSize: 12 }}>
                                        <thead>
                                            <tr><th colSpan={6} className='text-center table-info'>ACTIVIDADES</th></tr>
                                            <tr>
                                                <th>Desde</th>
                                                <th>Hasta</th>
                                                <th>Total</th>
                                                <th>Operación</th>
                                                <th>Actividad</th>
                                                <th>Observación</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {preview.actividades && preview.actividades.map((act, index) => (
                                                <tr key={index}>
                                                    <td>{act.desde}</td>
                                                    <td>{act.hasta}</td>
                                                    <td>{act.total}</td>
                                                    <td>{act.operacion}</td>
                                                    <td>{act.actividad}</td>
                                                    <td>{act.observacion}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>

                                    {/* Cuadrillas */}
                                    <div className="row">

                                        {preview.cuadrillas && Object.entries(preview.cuadrillas).map(([name, personal]) => (
                                            <div key={name} className="mb-3 col-lg-4 col-md-6 col-sm-12">
                                                <Table striped bordered hover size="sm" responsive style={{ fontSize: 12 }}>
                                                    <thead>
                                                        <tr><th colSpan={3} className='text-center table-info'>{name}</th></tr>
                                                        <tr>
                                                            <th>Cargo</th>
                                                            <th>Nombre</th>
                                                            <th>Horas</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {personal && personal.map((p, index) => (
                                                            <tr key={index}>
                                                                <td>{p.cargo}</td>
                                                                <td>{p.nombre}</td>
                                                                <td>{p.horas}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseInternal}>Cancelar</Button>
                <Button variant="primary" onClick={handleImport} disabled={errors.length > 0 || !parsedData}>Importar</Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ImportFromExcelModal;